import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import AvisoDelPanel, { BOTON_AVISO_PRINCIPAL, BOTON_AVISO_SECUNDARIO } from './panel/AvisoDelPanel';
import api from '../lib/api';
import { useDowngradeNotice } from '../hooks/useDowngradeNotice';
import { useRaizActiva } from '../lib/pager/activacion';

// Fire after any change that can affect the downgrade banner (a delete that drops the user
// under the new tope, etc.). Both the Dashboard list and this banner listen for it.
export const notifyDocsChanged = () => {
  window.dispatchEvent(new CustomEvent('studybuddy:docs-changed'));
};

// True per-day dismiss — localStorage stamped with today's LOCAL date (YYYY-MM-DD). It
// survives closing the browser and reappears the next calendar day (dismissed today =>
// hidden until tomorrow).
const DISMISS_KEY = 'downgradeBannerDismissed';
const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};
const isDismissedToday = () => {
  try { return localStorage.getItem(DISMISS_KEY) === todayStr(); } catch { return false; }
};

function formatDate(iso, lng) {
  try {
    return new Intl.DateTimeFormat(lng || 'en', { year: 'numeric', month: 'long', day: 'numeric' })
      .format(new Date(iso));
  } catch {
    return iso || '';
  }
}

/**
 * Scheduled-downgrade banner. Reads GET /payments/downgrade-notice (cache-backed, never
 * calls Stripe). Fail-safe: {show:false} or any error renders nothing — never blocks the
 * dashboard, no spinner. Refetches on focus/visibility (throttled) and on docs-changed, so
 * it appears/updates/disappears without a reload when the boundary passes, the user deletes
 * docs under the tope, or undoes the downgrade. In-flow (not fixed) so the mobile bottom-nav
 * never covers it.
 *
 * PAGER-1 (remate del censo) — la lectura ya no vive aquí: es el MISMO hook que
 * usa el Sidebar (`useDowngradeNotice`), ahora enrutado por `screenDataCache`
 * bajo la llave `downgrade-notice`. Antes eran DOS peticiones idénticas sin
 * cache ni dedupe con el Panel abierto; ahora es UNA. Este componente conserva
 * entero lo suyo —el cierre por día calendario, el "Keep my plan" optimista y
 * el render— y añade el gate de activación: es HIJO del Panel, así que una raíz
 * dormida no pide su aviso (el del Sidebar, que es chasis, nunca duerme).
 */
export default function DowngradeBanner() {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  const activa = useRaizActiva();
  const { notice } = useDowngradeNotice({ activo: activa });
  const [dismissed, setDismissed] = useState(isDismissedToday());
  const [hidden, setHidden] = useState(false); // optimistic hide after "Keep my plan"
  const [keeping, setKeeping] = useState(false);

  if (!notice.show || dismissed || hidden) return null;

  const { change_date, keep_count, block_count } = notice;
  const bodyKey = block_count === 1 ? 'downgrade_banner.body_one' : 'downgrade_banner.body_other';

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, todayStr()); } catch { /* ignore */ }
    setDismissed(true);
  };

  const onKeep = async () => {
    setKeeping(true);
    try {
      await api.post('/payments/undo-downgrade');
      // The cache clears via the subscription.updated webhook (may lag a beat), so hide
      // optimistically now; the next focus refetch confirms show:false.
      setHidden(true);
      notifyDocsChanged();
    } catch {
      // best-effort — leave the banner up if the undo failed.
    } finally {
      setKeeping(false);
    }
  };

  // dashboard-rediseno (D4, 15-oct-2026): el aviso gris del Panel nuevo (con su calendario),
  // entre el saludo y la tarjeta para agregar material: "Manage my documents", "Keep my plan" en
  // naranja y la X. Cuando sale y lo que hace cada boton no cambia.
  return (
    <AvisoDelPanel tono="gris" icono={CalendarDays} testid="downgrade-banner"
      titulo={t('downgrade_banner.title', { change_date: formatDate(change_date, i18n.language) })}
      texto={t(bodyKey, { keep_count, block_count })}
      acciones={(
        <>
          <button type="button" onClick={() => navigate('/manage-documents')} className={BOTON_AVISO_SECUNDARIO}
            data-testid="downgrade-banner-manage">
            {t('downgrade_banner.cta_manage')}
          </button>
          <button type="button" onClick={onKeep} disabled={keeping} className={BOTON_AVISO_PRINCIPAL}
            data-testid="downgrade-banner-keep">
            {t('downgrade_banner.cta_keep')}
          </button>
        </>
      )}
      alCerrar={onDismiss} etiquetaCerrar={t('downgrade_banner.dismiss_aria')} cerrarTestid="downgrade-banner-dismiss" />
  );
}
